/**
 * Call state store + global WS wiring (V3b — SM-9 client plane).
 *
 * The store is the single render source for the /call screen; every WS push
 * is a re-render hint, never a computed outcome (SM §0 — the server is the
 * referee; REST GET /calls/active is the recovery plane on reload).
 *
 * `initCallWiring()` runs once at module import (the router importing the
 * screen imports this) and keeps listening app-wide, so an incoming ring
 * surfaces (announcer + toast) whatever screen the user is on; the full
 * ring UI lives on /call.
 */
import { formatCopy, resolveCopy } from "@humble/shared";
import { announce, toast } from "../../design/index.js";
import { api } from "../../lib/api.js";
import { createStore } from "../../lib/store.js";
import { ws } from "../../lib/ws.js";

export type CallPhase = "idle" | "incoming" | "outgoing" | "connecting" | "active" | "ended";
export type CallKind = "voice" | "video";

export interface CallState {
  phase: CallPhase;
  callId: string | null;
  matchId: string | null;
  kind: CallKind;
  /** 'caller' | 'callee' for the live call. */
  role: "caller" | "callee" | null;
  peerName: string | null;
  /** SM-9 end_reason on phase='ended' (safety renders ©call.ended_generic). */
  endReason: string | null;
  /** Honest degraded-mode note (©call.turn_unavailable) when no TURN exists. */
  turnAvailable: boolean | null;
  error: string | null;
}

const initial: CallState = {
  phase: "idle",
  callId: null,
  matchId: null,
  kind: "voice",
  role: null,
  peerName: null,
  endReason: null,
  turnAvailable: null,
  error: null,
};

export const callStore = createStore<CallState>({ ...initial });

export function resetCall(): void {
  callStore.set({ ...initial });
}

/** Recovery plane: re-derive the live call after a reload / reconnect. */
export async function recoverActiveCall(): Promise<void> {
  try {
    // GET /calls/active is V3b's recovery-plane helper (routes/calls.ts) —
    // additive to openapi.yaml, hence the cast around the generated types.
    const res = (await api.get("/calls/active" as never)) as unknown;
    const call = (res as { call?: Record<string, unknown> | null }).call;
    if (!call) return;
    const state = String(call.state);
    callStore.set({
      callId: String(call.call_id),
      matchId: String(call.match_id),
      kind: call.kind === "video" ? "video" : "voice",
      role: call.role === "caller" ? "caller" : "callee",
      phase:
        state === "ringing"
          ? call.role === "caller"
            ? "outgoing"
            : "incoming"
          : state === "connecting"
            ? "connecting"
            : state === "active"
              ? "active"
              : "idle",
    });
  } catch {
    /* recovery is best-effort; the screen renders idle */
  }
}

let wired = false;
/** v3 — one sting/toast per ring: the server may re-push `call.incoming`
 *  while ringing; the state refresh is idempotent, the cosmetics must not
 *  stack (they were burying the Accept/Decline buttons under toasts). */
let lastRungCallId: string | null = null;

export function initCallWiring(): void {
  if (wired) return;
  wired = true;

  ws.on("call.incoming", (p) => {
    callStore.set({
      phase: "incoming",
      callId: p.call_id,
      matchId: p.match_id,
      kind: p.kind,
      role: "callee",
      peerName: p.caller?.display_name ?? null,
      endReason: null,
      error: null,
    });
    if (lastRungCallId === p.call_id) return; // re-pushed ring — no repeat cosmetics
    lastRungCallId = p.call_id;
    // Already looking at the ring UI → the screen IS the notification.
    if (typeof location !== "undefined" && location.pathname.startsWith("/call")) return;
    const text = formatCopy(resolveCopy("call.incoming") ?? "Incoming {kind} call — {name}", {
      kind: p.kind,
      name: p.caller?.display_name ?? "someone",
    });
    announce({ sting: text, motif: "gold" });
    toast(`${text} — open Calls to answer`, "announcer", 8000);
  });

  ws.on("call.state", (p) => {
    const s = callStore.get();
    if (s.callId !== p.call_id) return;
    if (p.state === "connecting") {
      callStore.set({ phase: "connecting" });
    } else if (p.state === "active") {
      callStore.set({ phase: "active" });
    } else if (p.state === "ended") {
      callStore.set({ phase: "ended", endReason: p.reason ?? null });
    }
  });

  ws.on("call.missed", (p) => {
    const s = callStore.get();
    if (s.callId === p.call_id) callStore.set({ phase: "ended", endReason: "missed" });
  });
}

initCallWiring();
